import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Warning, Microphone, FloppyDisk, Trash } from '@phosphor-icons/react';

interface RecordingNavigationGuardProps {
  open: boolean;
  onContinueRecording: () => void;
  onEndAndSave: () => void;
  onDiscard: () => void;
  isProcessing?: boolean;
  recordingDuration?: string;
}

export const RecordingNavigationGuard: React.FC<RecordingNavigationGuardProps> = ({
  open,
  onContinueRecording,
  onEndAndSave,
  onDiscard,
  isProcessing = false,
  recordingDuration
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onContinueRecording} // Close by default continues recording
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          margin: '16px',
          maxWidth: isMobile ? '90vw' : '400px',
          width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.2)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pb: 1,
          pt: 3,
          px: 3,
          color: '#dc2626',
          fontSize: '18px',
          fontWeight: 600
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: '#dc2626',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.5 }
            }
          }}
        />
        Recording in Progress
      </DialogTitle>

      <DialogContent sx={{ pb: 1, px: 3 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.5, fontSize: '14px', color: 'text.primary' }}>
          What would you like to do?
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          flexDirection: 'column',
          gap: 1.5,
          p: 3,
          pt: 1
        }}
      >
        {/* Return to Recording - Primary action */}
        <Button
          onClick={onContinueRecording}
          variant="contained"
          startIcon={<Microphone size={18} weight="fill" />}
          fullWidth
          sx={{
            backgroundColor: '#dc2626',
            height: '44px',
            fontSize: '15px',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#b91c1c'
            }
          }}
        >
          Return to Recording
        </Button>

        {/* End & Summarize */}
        <Button
          onClick={onEndAndSave}
          variant="outlined"
          startIcon={<FloppyDisk size={18} />}
          disabled={isProcessing}
          fullWidth
          sx={{
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            height: '44px',
            fontSize: '15px',
            fontWeight: 500,
            '&:hover': {
              borderColor: theme.palette.primary.dark,
              backgroundColor: theme.palette.primary.main + '08'
            }
          }}
        >
          {isProcessing ? 'Processing...' : 'End & Generate Summary'}
        </Button>

        {/* Discard */}
        <Button
          onClick={onDiscard}
          variant="text"
          startIcon={<Trash size={18} />}
          disabled={isProcessing}
          fullWidth
          sx={{
            color: theme.palette.error.main,
            height: '40px',
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: theme.palette.error.main + '08'
            }
          }}
        >
          Discard
        </Button>
      </DialogActions>
    </Dialog>
  );
};
